import { FC, useState, useMemo } from 'react';
import { z } from 'zod';

import { parThreeCountSelectOptions } from '@/api/parThree.types.ts';
import {
  CreateRewardTierData,
  RewardTier,
  RewardTierType,
} from '@/api/rewardTier/rewardTier.types';
import rewardTierIcon from '@/assets/rewardTierIcon.png';
import { EntityFormFieldSpecification } from '@/components/controls/CustomEntityFormField';
import CustomEntityFormModal from '@/components/controls/CustomEntityFormModal';
import CustomEntityTable, {
  Column,
} from '@/components/controls/CustomEntityTable';
import CustomConfirmToast from '@/components/toast/CustomConfirmToast';
import { RewardTierSchema } from '@/components/zod/types';
import { useRewardTiers } from '@/hooks/useRewardTiers';
import { useRewardTierStatusOptions } from '@/hooks/useRewardTierStatusOptions.ts';
import { formatZAR } from '@/lib/currency';
import { capitalizeText } from '@/utils/valueFormatter.ts';

type RewardTierFormData = z.infer<typeof RewardTierSchema>;

const formatCountOrNA = (value: unknown): string =>
  value === null || value === undefined ? 'N/A' : String(value);

const usesParThree = (type: RewardTierType): boolean =>
  type === 'standard' || type === 'corporate';

const rewardTierColumns: Column<RewardTier>[] = [
  {
    key: 'name',
    label: 'Name',
    sortable: true,
    width: 15,
  },
  {
    key: 'parThree',
    label: 'Par-3 Count',
    sortable: true,
    width: 12,
    formattingFunc: formatCountOrNA,
  },
  {
    key: 'playerCount',
    label: 'Player Count',
    sortable: true,
    width: 12,
    formattingFunc: formatCountOrNA,
  },
  {
    key: 'registrationFee',
    label: 'Registration Fee',
    sortable: true,
    width: 14,
    formattingFunc: formatZAR,
  },
  {
    key: 'prize',
    label: 'Prize',
    sortable: true,
    width: 12,
    formattingFunc: formatZAR,
  },
  {
    key: 'status',
    label: 'Status',
    sortable: true,
    width: 12,
    formattingFunc: capitalizeText,
  },
  {
    key: 'type',
    label: 'Type',
    sortable: true,
    width: 12,
    formattingFunc: capitalizeText,
  },
];

const RewardTierInfoBox: FC = () => (
  <div
    className="mb-4 rounded-lg px-5 py-4 text-sm leading-relaxed"
    style={{ backgroundColor: '#37445180', color: '#FFFFFF' }}
  >
    Reward tiers determine how rewards are earned based on the booking or
    voucher type. <span className="font-semibold">Standard</span> rewards apply
    to in-app round bookings. <span className="font-semibold">Corporate</span>{' '}
    rewards apply to both in-app round bookings and corporate voucher purchases.{' '}
    <span className="font-semibold">Custom</span> rewards are used exclusively
    for complimentary vouchers (where the Registration Fee and Par-3 Count are
    not applicable), and <span className="font-semibold">Individual</span>{' '}
    rewards apply to in-app voucher bundle purchases, which do not use the Par-3
    Count as part of the reward criteria.
  </div>
);

const RewardTierDashboard: FC = () => {
  const {
    rewardTiers,
    selectedRewardTier,
    setSelectedRewardTier,
    deleteTier,
    updateTier,
    createTier,
    isLoading,
    isFetchingAll,
  } = useRewardTiers();

  const { statusOptions } = useRewardTierStatusOptions();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const rewardTierFields: EntityFormFieldSpecification[] = useMemo(() => {
    const statusSelectOptions =
      statusOptions?.map((status) => ({
        label: status.name,
        value: status.id,
      })) || [];

    return [
      {
        name: 'name',
        label: 'Tier Name',
        type: 'text',
        placeholder: 'Enter tier name',
        validation: {
          required: true,
        },
      },
      {
        name: 'type',
        label: 'Tier Type',
        fieldType: 'select',
        selectOptions: [
          { label: 'Standard', value: 'standard' },
          { label: 'Corporate', value: 'corporate' },
          { label: 'Custom', value: 'custom' },
          { label: 'Individual', value: 'individual' },
        ],
        type: 'text',
        placeholder: 'Select tier type',
        searchable: false,
        validation: {
          required: true,
        },
      },
      {
        name: 'status',
        label: 'Status',
        fieldType: 'select',
        selectOptions: statusSelectOptions,
        type: 'text',
        placeholder: 'Select status',
        validation: {
          required: true,
        },
      },
      {
        name: 'parThree',
        label: 'Par-3 Count',
        fieldType: 'select',
        selectOptions: parThreeCountSelectOptions,
        searchable: false,
        type: 'text',
        placeholder: 'Select par-3 count',
        validation: {
          required: true,
        },
        watchFields: ['type'],
        dynamicHidden: (values) => !usesParThree(values[0] as RewardTierType),
      },
      {
        name: 'playerCount',
        label: 'Player Count',
        type: 'number',
        placeholder: 'Enter player count',
        validation: {
          required: true,
        },
        watchFields: ['type'],
        dynamicHidden: (values) => values[0] !== 'individual',
      },
      {
        name: 'registrationFee',
        label: 'Registration Fee (Rand)',
        type: 'number',
        placeholder: 'Enter registration fee',
        validation: {
          required: false,
        },
        watchFields: ['type'],
        dynamicDisabled: (values) => values[0] === 'custom',
      },
      {
        name: 'prize',
        label: 'Prize (Rand)',
        type: 'number',
        placeholder: 'Enter prize amount',
        validation: {
          required: true,
        },
      },
    ];
  }, [statusOptions]);

  const handleAdd = (): void => {
    setShowCreateModal(true);
  };

  const handleView = (rewardTier: RewardTier): void => {
    setSelectedRewardTier(rewardTier);
    setShowViewModal(true);
  };

  const handleDelete = (rewardTier: RewardTier): void => {
    setSelectedRewardTier(rewardTier);
    setShowDeleteModal(true);
  };

  const cancelDelete = (): void => {
    setShowDeleteModal(false);
    setSelectedRewardTier(null);
  };

  const confirmDelete = (): void => {
    if (selectedRewardTier) {
      deleteTier(selectedRewardTier.id);
    }
    setShowDeleteModal(false);
    setSelectedRewardTier(null);
  };

  const handleEditFormCancel = (): void => {
    setSelectedRewardTier(null);
  };

  const buildPayload = (data: RewardTierFormData): CreateRewardTierData => {
    const type = data.type as RewardTierType;

    return {
      name: data.name,
      type,
      registrationFee:
        type === 'custom' ? 0 : Number(data.registrationFee || 0),
      prize: data.prize,
      status: data.status,
      parThree: usesParThree(type) ? Number(data.parThree) : null,
      playerCount: type === 'individual' ? Number(data.playerCount) : null,
    };
  };

  const handleEditFormSubmit = (
    data: RewardTierFormData
    // eslint-disable-next-line consistent-return
  ): RewardTierFormData | void => {
    if (data.name && data.prize && selectedRewardTier) {
      const payload = buildPayload(data);

      updateTier(selectedRewardTier.id, payload);

      return { ...data, ...payload };
    }
  };

  const handleCreateFormSubmit = (data: RewardTierFormData): void => {
    if (data.name && data.prize) {
      createTier(buildPayload(data));
    }
    setShowCreateModal(false);
  };

  return (
    <>
      <CustomEntityTable
        title="Reward Tiers"
        columns={rewardTierColumns}
        data={rewardTiers}
        totalData={rewardTiers.length}
        iconName={rewardTierIcon}
        isLoading={isFetchingAll}
        onView={handleView}
        onDelete={handleDelete}
        onAdd={handleAdd}
        addButtonText="+ Add Reward Tier"
        subHeader={<RewardTierInfoBox />}
      />

      <CustomEntityFormModal
        isOpen={showViewModal}
        isLoading={isLoading}
        onClose={() => setShowViewModal(false)}
        schema={RewardTierSchema}
        entityData={selectedRewardTier}
        initialMode="view"
        fields={rewardTierFields}
        onSubmit={handleEditFormSubmit}
        onCancel={handleEditFormCancel}
        title="Reward Tier Details"
      />

      <CustomEntityFormModal
        isOpen={showCreateModal}
        isLoading={isLoading}
        onClose={() => setShowCreateModal(false)}
        schema={RewardTierSchema}
        entityData={
          { status: 'inactive', type: 'standard' } as RewardTierFormData
        }
        initialMode="create"
        fields={rewardTierFields}
        onSubmit={handleCreateFormSubmit}
        onCancel={() => setShowCreateModal(false)}
        title="Add Reward Tier"
      />

      <CustomConfirmToast
        header="Confirm Delete"
        show={showDeleteModal}
        message={`Are you sure you want to delete "${selectedRewardTier?.name}"?`}
        onAccept={confirmDelete}
        onDecline={cancelDelete}
      />
    </>
  );
};

export default RewardTierDashboard;
